import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import NavigationCenter from '../components/navigationCenter/NavigationCenter';
import ProcessingSidebar from '../components/navigationCenter/ProcessingSidebar';
import KeywordFooter from '../components/KeywordFooter';
import HomeFAQSection, { faqItems } from '../components/HomeFAQSection';
import useVideoProcessing from '../components/navigationCenter/useVideoProcessing';

const Home = ({
  isUserSignedIn,
  handleLogout,
  user,
  setIsUserSignedIn,
  setUser,
  doctors,
  setDoctors,
  insurances,
  setInsurances,
  bodily_parts,
  setBodilyParts,
  originalDoctors,
  setOriginalDoctors,
}) => {
  const videoProcessing = useVideoProcessing();

  const hasStartedProcessing =
    Boolean(videoProcessing.downloadToken) ||
    videoProcessing.loadingSteps.length > 0 ||
    videoProcessing.isProcessingFinished;

  const homepageJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': 'https://www.subtitletsunami.com/#organization',
        name: 'SubtitleTsunami',
        alternateName: [
          'Subtitle Tsunami',
          'AI Subtitle Generator',
          'Spanish Subtitle Generator',
        ],
        url: 'https://www.subtitletsunami.com/',
        logo: {
          '@type': 'ImageObject',
          url: 'https://www.subtitletsunami.com/BlueLogo.png',
        },
        contactPoint: [
          {
            '@type': 'ContactPoint',
            email: 'jimmy.lagattuta@gmail.com',
            contactType: 'customer support',
            availableLanguage: ['English', 'Spanish'],
          },
        ],
      },
      {
        '@type': 'WebSite',
        '@id': 'https://www.subtitletsunami.com/#website',
        url: 'https://www.subtitletsunami.com/',
        name: 'SubtitleTsunami',
        description:
          'SubtitleTsunami is an AI-powered English to Spanish subtitle generator for videos.',
        publisher: {
          '@id': 'https://www.subtitletsunami.com/#organization',
        },
      },
      {
        '@type': 'WebApplication',
        '@id': 'https://www.subtitletsunami.com/#webapplication',
        name: 'SubtitleTsunami',
        url: 'https://www.subtitletsunami.com/',
        applicationCategory: 'MultimediaApplication',
        operatingSystem: 'Web',
        browserRequirements: 'Requires a modern web browser.',
        description:
          'SubtitleTsunami helps creators generate English to Spanish subtitles for videos using AI.',
        image: 'https://www.subtitletsunami.com/BlueLogo.png',
        publisher: {
          '@id': 'https://www.subtitletsunami.com/#organization',
        },
        offers: {
          '@type': 'Offer',
          price: '4.99',
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock',
          url: 'https://www.subtitletsunami.com/',
        },
        featureList: [
          'Upload videos',
          'Generate English to Spanish subtitles',
          'Download subtitled videos',
          'Free daily upload with paid Starter upgrade',
        ],
      },
      {
        '@type': 'SoftwareApplication',
        '@id': 'https://www.subtitletsunami.com/#softwareapplication',
        name: 'SubtitleTsunami',
        applicationCategory: 'VideoApplication',
        operatingSystem: 'Web',
        url: 'https://www.subtitletsunami.com/',
        description:
          'AI-powered software for generating English to Spanish subtitles for video content.',
        image: 'https://www.subtitletsunami.com/BlueLogo.png',
        publisher: {
          '@id': 'https://www.subtitletsunami.com/#organization',
        },
        offers: {
          '@type': 'Offer',
          price: '4.99',
          priceCurrency: 'USD',
        },
      },
      {
        '@type': 'FAQPage',
        '@id': 'https://www.subtitletsunami.com/#faq',
        mainEntity: faqItems.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      },
    ],
  };

  const seoLearningLinks = [
    {
      title: 'English to Spanish Subtitle Generator',
      description:
        'Learn how SubtitleTsunami helps turn English videos into Spanish-subtitled downloads.',
      path: '/english-to-spanish-subtitle-generator',
      label: 'Main Generator Page',
    },
    {
      title: 'How to Add Spanish Subtitles to an English Video',
      description:
        'A step-by-step guide for adding Spanish subtitles to videos you own or have permission to use.',
      path: '/how-to-add-spanish-subtitles-to-video',
      label: 'How-To Guide',
    },
    {
      title: 'Translate YouTube Shorts into Spanish Subtitles',
      description:
        'A creator-focused guide for turning English Shorts into Spanish-subtitled videos.',
      path: '/translate-youtube-shorts-to-spanish-subtitles',
      label: 'Shorts Guide',
    },
    {
      title: 'Add Spanish Captions to TikTok and Instagram Reels',
      description:
        'Learn how Spanish captions can help short-form videos become easier to follow and reuse.',
      path: '/add-spanish-captions-to-tiktok-instagram-reels',
      label: 'Social Video Guide',
    },
  ];

  return (
    <main className="relative flex min-h-screen w-full flex-col overflow-hidden bg-slate-950 text-white">
      <SEO
        title="SubtitleTsunami - AI English to Spanish Subtitle Generator"
        description="Generate English to Spanish subtitles for videos with AI. Upload your video, create Spanish subtitles, and download your finished subtitled video."
        canonical="https://www.subtitletsunami.com/"
        jsonLd={homepageJsonLd}
      />

      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.22),transparent_34%),radial-gradient(circle_at_70%_15%,rgba(59,130,246,0.18),transparent_30%),linear-gradient(135deg,rgba(2,6,23,0.98),rgba(15,23,42,0.94),rgba(8,47,73,0.75))]" />

      <div className="absolute inset-0 opacity-30">
        <div className="absolute left-[-10%] top-[10%] h-72 w-72 rounded-full bg-cyan-400/20 blur-3xl" />
        <div className="absolute right-[-8%] top-[25%] h-96 w-96 rounded-full bg-blue-500/20 blur-3xl" />
        <div className="absolute bottom-[-10%] left-[25%] h-80 w-80 rounded-full bg-teal-400/10 blur-3xl" />
      </div>

      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.035)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.035)_1px,transparent_1px)] bg-[size:44px_44px] opacity-20" />

      <section
        className={[
          'relative z-10 mx-auto grid w-full flex-1 grid-cols-1 gap-6 px-4 py-6 sm:px-6 lg:px-8',
          hasStartedProcessing
            ? 'max-w-[1680px] 2xl:grid-cols-[minmax(0,1fr)_360px]'
            : 'max-w-7xl',
        ].join(' ')}
      >
        <div className="min-w-0">
          <div className="mb-6 flex flex-col gap-4 rounded-3xl border border-white/10 bg-slate-950/45 p-5 shadow-2xl shadow-cyan-950/30 backdrop-blur-xl sm:p-6">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.35em] text-cyan-300">
                Subtitle Tsunami
              </p>

              <h1 className="mt-2 text-3xl font-black tracking-tight text-white sm:text-4xl lg:text-5xl">
                Translate. Subtitle. Download.
              </h1>

              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300 sm:text-base">
                Upload a video, generate English to Spanish subtitles, and
                download the processed file from one clean production workflow.
              </p>
            </div>
          </div>

          <section className="min-w-0 flex-1 rounded-3xl border border-white/10 bg-slate-950/55 p-5 shadow-2xl shadow-slate-950/40 backdrop-blur-xl sm:p-6">
            <NavigationCenter
              user={user}
              isUserSignedIn={isUserSignedIn}
              handleLogout={handleLogout}
              setIsUserSignedIn={setIsUserSignedIn}
              setUser={setUser}
              setDoctors={setDoctors}
              setInsurances={setInsurances}
              setBodilyParts={setBodilyParts}
              setOriginalDoctors={setOriginalDoctors}
              videoProcessing={videoProcessing}
              hasStartedProcessing={hasStartedProcessing}
            />
          </section>

          <HomeFAQSection />

          <section className="mt-8 overflow-hidden rounded-3xl border border-cyan-300/15 bg-slate-950/55 p-5 shadow-2xl shadow-cyan-950/30 backdrop-blur-xl sm:p-6 lg:p-8">
            <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.32em] text-cyan-300">
                  Learn More
                </p>

                <h2 className="mt-2 text-2xl font-black tracking-tight text-white sm:text-3xl">
                  Learn More About Spanish Subtitles
                </h2>

                <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-300 sm:text-base">
                  Explore guides for adding Spanish subtitles to English videos,
                  YouTube Shorts, TikTok videos, Instagram Reels, and other
                  creator content.
                </p>
              </div>

              <Link
                to="/blog"
                className="inline-flex w-fit items-center justify-center rounded-2xl border border-cyan-300/20 bg-cyan-300/10 px-4 py-2 text-xs font-black uppercase tracking-[0.16em] text-cyan-100 shadow-lg shadow-cyan-950/20 transition hover:-translate-y-0.5 hover:border-cyan-200/60 hover:bg-cyan-300/15 hover:text-white"
              >
                View Blog
              </Link>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {seoLearningLinks.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="group relative overflow-hidden rounded-3xl border border-white/10 bg-slate-950/60 p-5 transition hover:-translate-y-1 hover:border-cyan-300/40 hover:bg-slate-900/80"
                >
                  <div className="absolute inset-0 translate-x-[-120%] bg-gradient-to-r from-transparent via-cyan-200/10 to-transparent transition duration-700 group-hover:translate-x-[120%]" />

                  <div className="relative">
                    <p className="mb-3 inline-flex rounded-full border border-cyan-300/15 bg-cyan-300/5 px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-cyan-200">
                      {item.label}
                    </p>

                    <h3 className="text-lg font-black leading-snug text-white sm:text-xl">
                      {item.title}
                    </h3>

                    <p className="mt-3 text-sm leading-6 text-slate-300">
                      {item.description}
                    </p>

                    <p className="mt-4 text-xs font-black uppercase tracking-[0.16em] text-cyan-300">
                      Read Guide →
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </div>

        {hasStartedProcessing && (
          <ProcessingSidebar
            className="hidden 2xl:block"
            loadingSteps={videoProcessing.loadingSteps}
            isUploading={videoProcessing.isUploading}
            isProcessingFinished={videoProcessing.isProcessingFinished}
            downloadToken={videoProcessing.downloadToken}
            selectedFile={videoProcessing.selectedFile}
            handleDownloadVideo={videoProcessing.handleDownloadVideo}
            buttonText={videoProcessing.buttonText}
            countdown={videoProcessing.countdown}
            formatCountdown={videoProcessing.formatCountdown}
          />
        )}
      </section>

      <KeywordFooter />
    </main>
  );
};

export default Home;